<template>
  <div>
    <form
      class="form"
      method="POST"
      action="javascript:;"
      @submit.prevent="submit"
    >
      <FormError v-if="formError.length > 0" :errorMessages="formError" />

      <FormLoading v-if="!formReady" />

      <CardBase v-else>
        <template #header>{{ $route.meta.title }}</template>
        <template #content>
          <FormRow class="mb-4" :required="true">
            <template #label>Asset</template>
            <template #field>
              <multiselect
                v-model="formData.asset"
                :options="listAsset"
                :loading="listAssetLoading"
                :close-on-select="true"
                :hide-selected="false"
                :allow-empty="false"
                :class="$v.formData.asset.$error ? 'multiselect--error' : ''"
                :disabled="!formCreate"
                track-by="id"
                label="name"
                tag-placeholder="Press enter to choose"
                placeholder="Choose asset"
                :showLabels="false"
                @input="chooseAsset"
              >
                <template #noResult>No results found.</template>
              </multiselect>
              <div
                class="form-error"
                v-if="$v.formData.asset.$error && formSubmitted"
              >
                <span
                  class="form-error__text"
                  v-if="!$v.formData.asset.required"
                  >This field is required.</span
                >
              </div>
            </template>
          </FormRow>

          <FormRow class="mb-4" :required="true">
            <template #label>File</template>
            <template #field>
              <div>
                <input
                  class="form__input border-0"
                  type="file"
                  @change="selectFile"
                  ref="file"
                />
                <div class="form-error" v-if="importFileError">
                  <span class="form-error__text"
                    >Only xlsx files are allowed.</span
                  >
                </div>
              </div>
            </template>
            <template #help>
              <a :href="fileFormatUrl" class="text-blue-500 hover:underline">
                Click here to download the file format.</a
              >
            </template>
          </FormRow>
        </template>

        <template #footer>
          <FormRow class="mb-0">
            <template #field>
              <BaseButton class="mr-2" type="submit">Submit</BaseButton>
            </template>
            <template #link>
              <div>
                <template>or</template>
                <router-link :to="{ name: 'BonusList' }" class="ml-2 link"
                  >Back to List Bonus</router-link
                >
              </div>
            </template>
          </FormRow>
        </template>
      </CardBase>
    </form>

    <CardBase class="mt-2" v-if="importSummary.rows > 0">
      <template #header>Results</template>
      <template #content>
        <div class="w-full ws:1/2">
          <div class="flex flex-wrap md:flex-no-wrap my-4">
            <InfoCard
              class="w-full md:w-1/3 bg-red-500 text-white"
              :icon="['fas', 'exclamation-triangle']"
              iconSize="4x"
              cardTitle="Failed Import"
              :cardTitleSub="importSummary.failed || '0'"
            />

            <InfoCard
              class="w-full md:w-1/3 mx-2 bg-green-500 text-white"
              :icon="['fas', 'check-circle']"
              iconSize="4x"
              cardTitle="Success Import"
              :cardTitleSub="importSummary.success || '0'"
            />

            <InfoCard
              class="w-full md:w-1/3 bg-gray-500 text-white"
              :icon="['fas', 'coins']"
              iconSize="4x"
              cardTitle="Total Import"
              :cardTitleSub="importSummary.rows || '0'"
            />
          </div>

          <h3 class="form__section-heading">Failed Username</h3>

          <FormNote class="mb-6"
            >Note: Jika panel ID berbeda / tidak ditemukan dalam asset yang
            dipilih, proses import akan gagal.</FormNote
          >

          <ul v-if="importSummary.failed >= 1">
            <li
              v-for="username in importSummary.failed_username"
              :key="username"
            >
              {{ username }}
            </li>
          </ul>
        </div>
      </template>
    </CardBase>
  </div>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import { getListAssetMixin } from "@/mixins/getListAssetMixin";
import { submitFormMixin } from "@/mixins/submitFormMixin";

import Multiselect from "vue-multiselect";

const prettyPrintJson = require("pretty-print-json");

export default {
  mixins: [getListAssetMixin, submitFormMixin],
  components: {
    Multiselect,
  },
  data: () => {
    return {
      listAsset: [],
      listAssetLoading: false,
      formData: {
        asset: null,
      },

      importSummary: {
        rows: 0,
        failed: 0,
        success: 0,
        failed_username: [],
      },

      fileFormatUrl: null,

      importFile: null,
      importFileError: false,
    };
  },
  validations: {
    formData: {
      asset: {
        required,
      },
    },
  },
  computed: {
    submitUrl() {
      return this.formCreate
        ? "/" + this.$route.meta.endpoint
        : "/" + this.$route.meta.endpoint + "/" + this.formData.id;
    },
    fetchEndPoint() {
      return this.$route.meta.endpoint;
    },
  },
  created() {
    if (this.formCreate || !this.formData.asset) {
      this.listAssetLoading = true;
      this.getListAsset();
    }

    this.fileFormatUrl = process.env.VUE_APP_CDN_URL + "/bonus-import.xlsx";
  },
  methods: {
    selectFile() {
      const file = this.$refs.file.files.item(0);

      if (
        file.type ==
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        this.importFile = this.$refs.file.files.item(0);
        this.importFileError = false;
      } else {
        this.$refs.file.value = null;
        this.importFile = null;
        this.importFileError = true;
      }
    },
    submit() {
      if (!this.formLoading) {
        this.formSubmitted = true;
        this.$v.$touch();

        if (this.$v.$invalid) {
          return;
        }

        this.formLoading = true;

        const method = this.formCreate ? "post" : "patch";
        const url = process.env.VUE_APP_API_URL + this.submitUrl;

        let form = new FormData();

        for (const [key, value] of Object.entries(this.formData)) {
          form.append(key, value);
        }

        if (this.importFile) {
          form.append("file", this.importFile);
        }

        this.$store.commit("toggleLoading");

        this.$http({
          method: method,
          url: url,
          data: form,
          headers: {
            "Content-Type": `multipart/form-data`,
          },
        })
          .then((res) => {
            if (res.status == 200 || res.status == 201) {
              this.importSummary = { ...this.importSummary, ...res.data.data };
              this.$swal({
                position: "bottom-end",
                showConfirmButton: false,
                toast: true,
                timer: 5000,
                timerProgressBar: true,
                title: res.data.data.rows + " data has been processed. Please check the results",
                icon: "success",
              });
              this.importFile = null;
              this.$refs.file.value = null;
              this.formData.asset_id = null;
              this.formData.asset = null;
            }
          })
          .catch((error) => {
            if (error.response) {
              this.formError = [];

              if (error.response.status == 404) {
                // this.formError[0] = '<pre>' + error.response.data.meta + '</pre>';
              } else if (error.response.status == 409) {
                // this.formError[0] = error.response.data;
              } else {
                const { data } = error.response.data;
                for (var err in data) {
                  this.formError.push(data[err]);
                }
              }
            }
          })
          .finally(() => {
            this.$store.commit("toggleLoading");
            this.formLoading = false;
            this.formSubmitted = false;
          });
      }
    },
    chooseAsset(selectedOption) {
      this.formData.asset_id = selectedOption.id;
    },
    printJson(data) {
      return data ? prettyPrintJson.toHtml(JSON.parse(data)) : "-";
    },
  },
};
</script>
